/* ⚠️ GENERATED from app/services/sessionGate.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   sessionGate.ts — may this device stay signed in? One answer, both clients.

   "Sometimes, when my internet is bad and I update the app, it asks me to
   approve the new login from an old device, or to send a code to my email."

   🟥 TWO QUESTIONS WERE ANSWERED BY ONE FIELD. `sessions/{device}.trusted` was
      read as BOTH "this device may stay signed in" AND "this device may approve
      other devices and manage sessions". The approval card's checkbox says the
      second ("Allows this device to manage active sessions") and defaults to
      OFF — so a login approved without ticking it was created `trusted:false`,
      and the gate then read that as "not allowed in": the device was sent back
      to the approval screen on its very next cold start. The self-heal that
      recreates a missing session row writes `trusted:false` too (correctly — a
      heal is not an approval), with the same effect. Every "approve again"
      loop the user has lived through has one of these underneath it.

   🟥 A CACHE MISS WAS READ AS "NO SESSION". On a bad network Firestore gives
      up on the server quickly and answers from its cache, which on a cold start
      is EMPTY — so "your session row does not exist" came back for a row that
      has existed for months. The gate then counted the other devices (also from
      the empty cache, or from the server if the radio came back in between) and
      either auto-trusted a device it should not have, or demanded approval for
      one it had approved long ago.

   🟥 AND A FAILED COUNT OF THE OTHER DEVICES "DEFAULTED TO APPROVAL" even
      when this device had been let in a hundred times before.

   🟢 THE MODEL, STATED ONCE:
      · APPROVED — the device may stay signed in. True when its session row is
        on the server and not force-logged-out, whatever `trusted` says. Once
        earned it is remembered locally, so a radio that will not answer never
        costs an approval that was already given.
      · TRUSTED — the device may approve others and manage sessions. That is
        `trusted === true`, and nothing here changes how it is earned.
      · Only an answer FROM THE SERVER can take approval away: a force-logout,
        or a row that the server itself says is missing while another trusted
        device exists. Cache answers, timeouts and failed reads are "unknown",
        and unknown falls back to what this device last knew about itself.

   The app's verifySessionOnline and the web's verifySession both ask this
   function and act on its verdict, so the two clients cannot drift apart.
   ═══════════════════════════════════════════════════════════════════════════ */

/** How a read went: an answer from the server, from a local cache, or no answer. */

const authoritative = r => r === 'server';
export function sessionVerdict(f) {
  const fallback = reason => f.locallyApproved || f.locallyTrusted ? {
    go: 'in',
    trusted: f.locallyTrusted,
    writeFirstDevice: false,
    resetForceLogout: false,
    healRow: false,
    remember: 'keep',
    reason: `${reason}:local_approved`
  } : {
    go: 'approve',
    trusted: false,
    writeFirstDevice: false,
    resetForceLogout: false,
    healRow: false,
    remember: 'keep',
    reason: `${reason}:never_approved`
  };

  /* 1. no server answer about our own row → what we last knew */
  if (!authoritative(f.read)) return fallback(f.read === 'cache' ? 'cache_answer' : 'read_failed');

  /* 2. the row is there */
  if (f.exists) {
    if (f.forceLogout) {
      if (f.trusted) {
        return {
          go: 'kicked',
          trusted: true,
          writeFirstDevice: false,
          resetForceLogout: false,
          healRow: false,
          remember: 'clear',
          reason: 'force_logout'
        };
      }
      return {
        go: 'approve',
        trusted: false,
        writeFirstDevice: false,
        resetForceLogout: true,
        healRow: false,
        remember: 'clear',
        reason: 'force_logout_untrusted'
      };
    }
    /* 🟢 approved. `trusted` is a privilege, not the ticket in. */
    return {
      go: 'in',
      trusted: f.trusted === true,
      writeFirstDevice: false,
      resetForceLogout: false,
      healRow: false,
      remember: 'approved',
      reason: f.trusted ? 'session_trusted' : 'session_approved'
    };
  }

  /* 3. the server says our row is missing */
  if (!authoritative(f.othersRead)) return fallback('others_unknown');
  if (f.otherTrustedExists) {
    /* 🟥 A ROW THE SERVER LOST IS NOT A DEVICE THE ACCOUNT NEVER SAW. The local
       approval memory is written only after a real approval or a server 'in'
       — and the only way an owner removes a device is force-logout, which
       clears that memory on the device itself. So a device that remembers being
       let in and finds no row has lost its row (a write that failed at approval,
       a row deleted by an older build) — the same case the in-session watchdog
       already heals by re-creating the row, NOT trusted. Heal it here too,
       rather than asking the owner to approve a phone they approved already. */
    if (f.locallyApproved) {
      return {
        go: 'in',
        trusted: false,
        writeFirstDevice: false,
        resetForceLogout: false,
        healRow: true,
        remember: 'approved',
        reason: 'healed_row'
      };
    }
    return {
      go: 'approve',
      trusted: false,
      writeFirstDevice: false,
      resetForceLogout: false,
      healRow: false,
      remember: 'clear',
      reason: 'new_device'
    };
  }
  return {
    go: 'in',
    trusted: true,
    writeFirstDevice: true,
    resetForceLogout: false,
    healRow: false,
    remember: 'approved',
    reason: 'first_device'
  };
}

/** Where the local memory of approval lives — per ACCOUNT, so a device approved
 *  for one account never lets a different account in while offline. */
export const approvedKeyFor = uid => `is_device_approved:${String(uid)}`;

/** Classify a Firestore snapshot read for `SessionFacts.read`. */
export const readSourceOf = (snap, failed) => failed || !snap ? 'failed' : snap.metadata && snap.metadata.fromCache ? 'cache' : 'server';
